import { zodResolver } from '@hookform/resolvers/zod'
import { DialogContent, FormControl, FormControlLabel, FormHelperText, Stack, Switch } from '@mui/material'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { getUpdateActivationGroupSchema } from '@/features/activationGroup/schemas'
import type {
  ActivationGroup,
  ApiActivationGroupResource,
  UpdateActivationGroup,
} from '@/features/activationGroup/types'
import {
  ActivationGroupState,
  ActivationProvider,
  ApiVolumeRangeUnit,
  ResourceCategory,
} from '@/features/activationGroup/types'

type UpdateActivationGroupDialogFormProps = {
  activationGroup: ActivationGroup
  resources: ApiActivationGroupResource[]
  isLoading: boolean
  onCancel: () => void
  onSubmit: (data: UpdateActivationGroup) => void
}

/**
 * Contains all the logic related to the form to modify an existing activation group.
 *
 * It is used within the EditActivationGroupDialog component.
 */
const UpdateActivationGroupDialogForm = ({
  isLoading,
  onCancel,
  onSubmit,
  activationGroup,
  resources,
}: UpdateActivationGroupDialogFormProps) => {
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      name: activationGroup.code,
      activationProvider: activationGroup.activationProvider ?? ActivationProvider.SYMPOWER_CENTRAL,
      resourceCategory: activationGroup.resourceCategory ?? ResourceCategory.MIXED,
      biddableVolumeRange: {
        min: activationGroup.biddableVolumeRange?.min ?? 0,
        max: activationGroup.biddableVolumeRange?.max ?? 0,
        unit: activationGroup.biddableVolumeRange?.unit.toUpperCase() as ApiVolumeRangeUnit,
      },
      state: activationGroup.state,
      regulationObjectCode: activationGroup.regulationObjectCode ?? null,
      transmissionArea: activationGroup.transmissionArea ?? null,
      tradingStrategy: activationGroup.tradingStrategy ?? null,
      reportingCode: activationGroup.reportingCode ?? null,
      externalCode: activationGroup.externalCode ?? null,
    },
    resolver: zodResolver(getUpdateActivationGroupSchema(t, activationGroup.portfolio.countryCode)),
  })
  const isFinland = activationGroup.portfolio.countryCode === 'FI'
  const resourcesAreQualified = useMemo(
    () => resources.every((resource) => resource.qualifiedCapacity != null && resource.qualifiedCapacity !== 0),
    [resources],
  )

  function handleSubmit(updateActivationGroup: UpdateActivationGroup) {
    onSubmit(updateActivationGroup)
  }

  return (
    <FormProvider {...form}>
      <form noValidate aria-label={t('activation_groups.update_form.title')} onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextFieldController required id="name" label={t('common.name')} name="name" />

          <SelectFieldController
            required
            disabled={activationGroup.activationProvider != null}
            id="activation-provider"
            label={t('activation_groups.activation_provider_label')}
            name="activationProvider"
            options={Object.values(ActivationProvider).map((provider) => ({
              value: provider,
              label: t(
                `activation_groups.activation_provider.${provider.toLowerCase() as Lowercase<ActivationProvider>}`,
              ),
              description: t(
                `activation_groups.activation_provider_description.${provider.toLowerCase() as Lowercase<ActivationProvider>}`,
              ),
              id: provider,
            }))}
          />

          <SelectFieldController
            required
            id="resource-category"
            label={t('activation_groups.create_form.resource_category_label')}
            name="resourceCategory"
            options={Object.values(ResourceCategory).map((category) => ({
              value: category,
              label: t(`activation_groups.resource_category.${category.toLowerCase() as Lowercase<ResourceCategory>}`),
              id: category,
            }))}
          />

          <Stack flexDirection="column" gap={3}>
            <CustomTypography variant="h3">{t('activation_groups.create_form.bidding_limits_label')}</CustomTypography>
            <Stack flexDirection="column" gap={3}>
              <TextFieldController
                id="biddable-volume-range-min"
                label={t('activation_groups.create_form.biddable_volume_range_min_label')}
                name="biddableVolumeRange.min"
                type="number"
              />
              <TextFieldController
                id="biddable-volume-range-max"
                label={t('activation_groups.create_form.biddable_volume_range_max_label')}
                name="biddableVolumeRange.max"
                type="number"
              />
              <SelectFieldController
                fullWidth
                id="biddable-volume-range-unit"
                label={t('activation_groups.create_form.biddable_volume_range_unit_label')}
                name="biddableVolumeRange.unit"
                options={Object.values(ApiVolumeRangeUnit).map((unit) => ({
                  value: unit,
                  label: t(
                    `activation_groups.volume_range_units.${unit.toLowerCase() as Lowercase<ApiVolumeRangeUnit>}`,
                  ),
                  id: unit,
                }))}
              />
            </Stack>
          </Stack>

          <Stack flexDirection="column" gap={3}>
            <CustomTypography variant="h3">
              {t('activation_groups.create_form.market_attributes_label')}
            </CustomTypography>

            <TextFieldController
              disabled={Boolean(activationGroup.regulationObjectCode)}
              id="regulation-object-code"
              label={t('activation_groups.create_form.regulation_object_code_label')}
              name="regulationObjectCode"
              type="text"
            />

            <TextFieldController
              id="trading-strategy"
              label={t('activation_groups.create_form.trading_strategy_label')}
              name="tradingStrategy"
              type="text"
            />

            <TextFieldController
              id="external-code"
              label={t('activation_groups.create_form.external_code_label')}
              name="externalCode"
              type="text"
            />
          </Stack>

          {isFinland && (
            <Stack flexDirection="column" gap={3}>
              <CustomTypography variant="h3">
                {t('activation_groups.create_form.finland_attributes_label')}
              </CustomTypography>

              <SelectFieldController
                fullWidth
                disabled={Boolean(activationGroup.transmissionArea)}
                id="transmission-area"
                label={t('activation_groups.create_form.transmission_area_label')}
                name="transmissionArea"
                options={['north', 'central', 'south'].map((value) => ({
                  id: value,
                  value,
                  label: t(`activation_groups.create_form.transmission_area.${value as 'north' | 'central' | 'south'}`),
                }))}
              />

              <TextFieldController
                id="reporting-code"
                label={t('activation_groups.create_form.reporting_code_label')}
                name="reportingCode"
                type="text"
              />
            </Stack>
          )}

          <FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.watch('state') === ActivationGroupState.LIVE}
                  data-testid="activation-group-state-switch"
                  name="state"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    form.setValue(
                      'state',
                      event.target.checked ? ActivationGroupState.LIVE : ActivationGroupState.NOT_LIVE,
                    )
                  }
                />
              }
              label={t('activation_groups.create_form.is_live_label')}
              sx={{ m: 0 }}
            />
            {!resourcesAreQualified && (
              <FormHelperText sx={{ color: 'warning.main' }}>
                {t('activation_groups.create_form.non_qualified_resources_helper')}
              </FormHelperText>
            )}
          </FormControl>
        </DialogContent>

        <FormDialogActions isLoading={isLoading} onCancel={onCancel} />
      </form>
    </FormProvider>
  )
}

export default UpdateActivationGroupDialogForm
